// src/data/stockTopUp.ts
// ----------------------
// Repository for the Stock Top-up capture tables (db-155): the atomic submit RPC
// plus readers for stock_top_up_headers / stock_top_up_lines. The module stays
// ISOLATED (stock-top-ups plan §0) — this wraps ONLY the top-up tables + RPC, and
// nothing here touches the standard fit-out request path. Typed via getTypedClient().

import { sortTopUpLines } from "../services/stockTopUpForm";
import type { Database, Json } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";
import { listStockItems } from "./stockItems";

type RequestViewRow = Database["public"]["Views"]["requests_view"]["Row"];
export type StockTopUpLineRow = Database["public"]["Tables"]["stock_top_up_lines"]["Row"];
export type StockTopUpHeaderRow = Database["public"]["Tables"]["stock_top_up_headers"]["Row"];

type RpcResult<T> = { data: T | null; error: { message: string } | null };

// Submit a Stock Top-up in one transaction: the requests row (request_type =
// 'stock_top_up'), its cover-sheet header (Notes), and the captured lines. Runs
// submit_stock_top_up_atomic as the INVOKER, so the requests INSERT policy
// (user_can_submit_for_shelter_id) enforces membership — there is no staff bypass.
// Returns the new requests_view row (dbToReq-able) + error, mirroring the
// upsert_request_atomic wrapper. Null client → error-free no-op (null data).
export async function submitStockTopUp(
  request: object,
  lines: object[]
): Promise<RpcResult<RequestViewRow>> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  try {
    const { data, error } = await c.rpc("submit_stock_top_up_atomic", {
      p_request: request as Json,
      p_lines: (lines ?? []) as Json,
    });
    if (error) logger.warn("submit_stock_top_up_atomic failed:", error.message);
    return { data: (data as unknown as RequestViewRow) ?? null, error };
  } catch (e) {
    logger.warn("submit_stock_top_up_atomic error:", e);
    return { data: null, error: { message: String(e) } };
  }
}

// The captured lines for a request (section then item_key order). RLS-gated by
// user_can_access_request: the owning shelter OR staff/admin. [] on error / no client.
export async function listStockTopUpLines(requestId: number): Promise<StockTopUpLineRow[]> {
  const c = getTypedClient();
  if (!c) return [];
  // Sort into request/capture order (sections in order; Bulk by the catalogue order,
  // the fixed matrices by their matrix order) — see sortTopUpLines. Without this, bulk
  // lines come back keyed on their stock_item_id (a UUID) and scatter.
  const [res, stockItems] = await Promise.all([
    c.from("stock_top_up_lines").select("*").eq("request_id", requestId),
    listStockItems(true),
  ]);
  const { data, error } = res;
  if (error) {
    logger.warn("listStockTopUpLines failed:", error.message);
    return [];
  }
  return sortTopUpLines((data ?? []) as unknown as StockTopUpLineRow[], stockItems);
}

// The cover-sheet header (the Other/Notes box) for a request, or null. RLS-gated.
export async function getStockTopUpHeader(requestId: number): Promise<StockTopUpHeaderRow | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("stock_top_up_headers")
    .select("*")
    .eq("request_id", requestId)
    .maybeSingle();
  if (error) {
    logger.warn("getStockTopUpHeader failed:", error.message);
    return null;
  }
  return (data as unknown as StockTopUpHeaderRow) ?? null;
}

// A lean row for the Top-up Task List (the approval queue).
export interface TopUpRequestRow {
  id: number;
  ref_no: string | null;
  shelter_name: string | null;
  shelter_code: string | null;
  state: string | null;
  status: string | null;
  date: string | null;
  created_at: string | null;
}

// Typed as `string` so supabase-js doesn't deep-parse the select.
const TOP_UP_LIST_COLS: string = "id,ref_no,shelter_name,shelter_code,state,status,date,created_at";

// Every stock-top-up request, newest first — the Top-up Task List queue (its own
// queue, isolated from the Responders list). RLS already scopes reads: staff see
// all, a shelter only its own. [] on error / no client.
export async function listTopUpRequests(): Promise<TopUpRequestRow[]> {
  const c = getTypedClient();
  if (!c) return [];
  const { data, error } = await c
    .from("requests_view")
    .select(TOP_UP_LIST_COLS)
    .eq("request_type", "stock_top_up")
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) {
    logger.warn("listTopUpRequests failed:", error.message);
    return [];
  }
  return (data ?? []) as unknown as TopUpRequestRow[];
}

// The header context the Top-up approval view shows above the section tabs.
export interface TopUpRequestDetailRow {
  id: number;
  ref_no: string | null;
  shelter_id: string | null;
  shelter_ref: string | null;
  shelter_name: string | null;
  shelter_code: string | null;
  shelter_email: string | null;
  state: string | null;
  status: string | null;
  date: string | null;
  // Shown with the date + time under the approval view's title ("ZephyrWA044 - Submitted …").
  created_at: string | null;
  delivery_address_resolved: string | null;
  delivery_address_id: string | null;
  delivery_address_override: string | null;
  // Delivery-address status flags (already in requests_view): used by the top-up
  // header's delivery block to show the verify/ban/unregistered warnings, same as
  // the responder request view.
  delivery_address_verified: boolean | null;
  delivery_address_banned: boolean | null;
  non_registered_address: boolean | null;
  contact_name: string | null;
  phone: string | null;
  mobile: string | null;
  caseworker_note: string | null;
}

const TOP_UP_DETAIL_COLS: string =
  "id,ref_no,shelter_id,shelter_ref,shelter_name,shelter_code,shelter_email,state,status,date,created_at,delivery_address_resolved,delivery_address_id,delivery_address_override,delivery_address_verified,delivery_address_banned,non_registered_address,contact_name,phone,mobile,caseworker_note";

// One top-up request's header row (for the approval view), or null. RLS-scoped.
export async function getTopUpRequestById(id: number): Promise<TopUpRequestDetailRow | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("requests_view")
    .select(TOP_UP_DETAIL_COLS)
    .eq("id", id)
    .maybeSingle();
  if (error) {
    logger.warn("getTopUpRequestById failed:", error.message);
    return null;
  }
  return (data as unknown as TopUpRequestDetailRow) ?? null;
}

// The staged per-line approval a Confirm & Send writes.
export interface TopUpLineApproval {
  id: string;
  approved_qty: number | null;
  approval_status: string; // 'approved' | 'declined' | 'pending'
}

// Confirm & Send: write every line's approval + move the request to a terminal status,
// in one transaction (db-157). Staff-only (the RPC role-guards). Returns the updated
// requests_view row + error. Null client → error-free no-op.
export async function confirmStockTopUp(
  requestId: number,
  lines: TopUpLineApproval[],
  status: string
): Promise<RpcResult<RequestViewRow>> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  try {
    const { data, error } = await c.rpc("confirm_stock_top_up_atomic", {
      p_request_id: requestId,
      p_lines: (lines ?? []) as unknown as Json,
      p_status: status,
    });
    if (error) logger.warn("confirm_stock_top_up_atomic failed:", error.message);
    return { data: (data as unknown as RequestViewRow) ?? null, error };
  } catch (e) {
    logger.warn("confirm_stock_top_up_atomic error:", e);
    return { data: null, error: { message: String(e) } };
  }
}
